import { Edit2 } from "lucide-react";
import { memo, useEffect, useRef, useState } from "react";
import { getLocalStorageUser } from "src/helpers/api/sharedFunction";
import PortfolioManagerModal from "./PortfolioManagerModal";

export interface PortfolioFolder {
  id: number;
  name: string;
  investment?: number;
}

interface PortfolioFolderProps {
  portfolioFolders: PortfolioFolder[];
  setPortfolioFolders: React.Dispatch<React.SetStateAction<PortfolioFolder[]>>;
  selectedFolders: string[];
  setSelectedFolders: React.Dispatch<React.SetStateAction<string[]>>;
  error?: React.ReactNode;
  token?: string;
  targetIdValue?: string | number | (string | number)[] | null;
}

const MAX_PORTFOLIOS = Number(process.env.NEXT_PUBLIC_MAX_PORTFOLIOS) || 15;

const PortfolioFolderSelector = ({
  portfolioFolders,
  setPortfolioFolders,
  selectedFolders,
  setSelectedFolders,
  error,
  token,
  targetIdValue,
}: PortfolioFolderProps) => {
  const [showFolderDropdown, setShowFolderDropdown] = useState(false);
  const user = getLocalStorageUser();
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<PortfolioFolder | null>(
    null,
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowFolderDropdown(false);
      }
    };

    if (showFolderDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFolderDropdown]);

  const toggleFolderSelection = (folderId: number) => {
    const idStr = String(folderId);
    setSelectedFolders((prev) => {
      const newSelection = prev.includes(idStr)
        ? prev.filter((f) => f !== idStr)
        : [idStr];
      localStorage.setItem(
        "last_selected_portfolio_folders",
        JSON.stringify(newSelection),
      );
      return newSelection;
    });
  };

  const getSelectedDisplayText = () => {
    if (selectedFolders.length === 0) {
      return portfolioFolders.length === 0
        ? "No portfolios"
        : "Select portfolio";
    }
    const names = portfolioFolders
      .filter((f) => selectedFolders.includes(String(f.id)))
      .map((f) => f.name);
    return names.length <= 2 ? names.join(", ") : `${names.length} selected`;
  };

  const appliedTargetOnce = useRef(false);
  useEffect(() => {
    if (
      !appliedTargetOnce.current &&
      targetIdValue &&
      portfolioFolders.length > 0
    ) {
      if (
        portfolioFolders.some((f) => String(f.id) === String(targetIdValue))
      ) {
        setSelectedFolders([String(targetIdValue)]);
      }
      appliedTargetOnce.current = true;
    } else if (
      !targetIdValue &&
      portfolioFolders.length > 0 &&
      selectedFolders.length === 0
    ) {
      try {
        const saved = localStorage.getItem("last_selected_portfolio_folders");
        if (saved) {
          const parsed = JSON.parse(saved);
          const validIds = parsed.filter((id: string) =>
            portfolioFolders.some((f) => String(f.id) === id),
          );
          if (validIds.length > 0) {
            setSelectedFolders(validIds);
          } else {
            setSelectedFolders([String(portfolioFolders[0].id)]);
          }
        } else {
          setSelectedFolders([String(portfolioFolders[0].id)]);
        }
      } catch (_e) {
        setSelectedFolders([String(portfolioFolders[0].id)]);
      }
    }
  }, [
    targetIdValue,
    portfolioFolders,
    selectedFolders.length,
    setSelectedFolders,
  ]);
  return (
    <>
      <div ref={dropdownRef} className="mb-2">
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Portfolio Folder *
          </label>
          {!user?.is_trial && (
            <div className="relative group mb-0 flex flex-col items-center">
              <button
                type="button"
                onClick={() => {
                  if (portfolioFolders.length < MAX_PORTFOLIOS) {
                    setEditingFolder(null);
                    setIsManageModalOpen(true);
                  }
                }}
                className={`text-sm  underline  ${
                  portfolioFolders.length >= MAX_PORTFOLIOS
                    ? "cursor-not-allowed opacity-60"
                    : "text-green-600 dark:text-green-400 hover:opacity-90 cursor-pointer"
                }`}
              >
                Manage Portfolios
              </button>
              {portfolioFolders.length >= MAX_PORTFOLIOS && (
                <div className="relative">
                  <div className="absolute hidden left-[-54px] -translate-x-1/2 mt-2 z-1000 w-64 sm:w-max bg-[#30324e] text-white text-sm p-2 rounded-md shadow-lg border border-[#394564] group-hover:block">
                    Contact Us for Customized Plan
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full w-0 h-0 border-l-6 border-r-6 border-b-8 border-l-transparent border-r-transparent border-b-[#30324e]" />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="relative">
          <div
            onClick={() =>
              portfolioFolders.length > 0 &&
              setShowFolderDropdown(!showFolderDropdown)
            }
            className={`w-full pl-4 pr-4 py-2 bg-[#edf1fc] dark:bg-black border border-[#a0a5bb] rounded-md text-sm font-figtree dark:text-white flex justify-between items-center ${
              portfolioFolders.length === 0
                ? "cursor-not-allowed opacity-70"
                : "cursor-pointer"
            }`}
          >
            <span className="truncate">{getSelectedDisplayText()}</span>
            {portfolioFolders.length > 0 && (
              <svg
                className="w-4 h-4 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            )}
          </div>

          {showFolderDropdown && (
            <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-[#1E1E2D] border border-[#a0a5bb] rounded-md shadow-lg max-h-60 overflow-y-auto">
              {portfolioFolders.map((folder) => (
                <div
                  key={folder.id}
                  className="flex items-center px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm group"
                >
                  <label className="flex items-center flex-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedFolders.includes(String(folder.id))}
                      onChange={() => toggleFolderSelection(folder.id)}
                      className="mr-3 h-4 w-4 text-green-600 rounded focus:ring-green-500"
                    />
                    <div className="flex-1">
                      <span className="dark:text-white block">
                        {folder.name}
                      </span>
                    </div>
                  </label>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingFolder(folder);
                      setIsManageModalOpen(true);
                      setShowFolderDropdown(false);
                    }}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Edit Portfolio Name"
                  >
                    <Edit2
                      size={14}
                      className="text-gray-500 dark:text-gray-400"
                    />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        {error && <div className="mt-1 text-sm text-red-500">{error}</div>}
      </div>
      <PortfolioManagerModal
        isOpen={isManageModalOpen}
        onClose={() => setIsManageModalOpen(false)}
        token={token}
        editingFolder={editingFolder}
        onSuccess={(folder, isEdit) => {
          if (isEdit) {
            setPortfolioFolders((prev) =>
              prev.map((f) => (f.id === folder.id ? folder : f)),
            );
          } else {
            setPortfolioFolders((prev) => [...prev, folder]);
            setSelectedFolders([String(folder.id)]);
            localStorage.setItem(
              "last_selected_portfolio_folders",
              JSON.stringify([String(folder.id)]),
            );
          }
        }}
      />
    </>
  );
};

export default memo(PortfolioFolderSelector);
